import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import useCustomerOptions from '../../../hooks/useCustomerOptions';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../../../utils/deviceDedup';
import { db } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import { syncBrandAndModel } from '../../../utils/brandSync';
import { invalidateCollections } from '../../../hooks/useCachedCollection';
import { useDeviceBrands } from '../../devices';

export const useAppointmentForm = (isOpen, onClose, randevuToEdit) => {
    const { currentUser } = useAuth();
    const isEditing = Boolean(randevuToEdit);

    // State
    // Müşteri listesi: paylaşımlı oturum cache'i (form açılışında tam koleksiyon okuması yok).
    const { customers } = useCustomerOptions();
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerInputValue, setCustomerInputValue] = useState('');
    const [telefon, setTelefon] = useState('');

    // Device State
    const [addDevice, setAddDevice] = useState(false);
    const [seriNumarasi, setSeriNumarasi] = useState('');
    const [marka, setMarka] = useState('');
    const [model, setModel] = useState('');
    const [cihazSinifi, setCihazSinifi] = useState('');
    const [isSgk, setIsSgk] = useState(false);
    const [zimmetTarihi, setZimmetTarihi] = useState('');
    const [faturaNo, setFaturaNo] = useState('');
    const [verilisTarihi, setVerilisTarihi] = useState('');
    const [garantiBaslangic, setGarantiBaslangic] = useState('');
    const [garantiBitis, setGarantiBitis] = useState('');
    const [periyodikBakimTarihi, setPeriyodikBakimTarihi] = useState('');
    const { brandsFromDb } = useDeviceBrands();

    // Appointment State
    const [randevuTarihi, setRandevuTarihi] = useState(null);
    const [ilgilenilenHizmet, setIlgilenilenHizmet] = useState('');
    const [notlar, setNotlar] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Düzenleme modunda, müşteri listesi hazır olduğunda mevcut müşteriyi seç.
    useEffect(() => {
        if (!isOpen || !isEditing || !randevuToEdit) return;
        const currentCustomer = customers.find(c => c.id === randevuToEdit.musteriId);
        setSelectedCustomer(currentCustomer || null);
        setCustomerInputValue(randevuToEdit.musteriAdSoyad || '');
    }, [isOpen, isEditing, randevuToEdit, customers]);

    // Check Edit Mode Data
    useEffect(() => {
        if (isOpen) {
            if (isEditing && randevuToEdit) {
                setRandevuTarihi(randevuToEdit.randevuTarihi ? randevuToEdit.randevuTarihi.toDate() : null);
                setIlgilenilenHizmet(randevuToEdit.ilgilenilenHizmet || '');
                setNotlar(randevuToEdit.notlar || '');
                setTelefon(randevuToEdit.musteriTelefon || '');
            } else {
                resetForm();
            }
        }
    }, [isOpen, randevuToEdit, isEditing]);

    const resetForm = () => {
        setSelectedCustomer(null);
        setCustomerInputValue('');
        setTelefon('');
        setRandevuTarihi(null);
        setIlgilenilenHizmet('');
        setNotlar('');
        setIsSaving(false);
        setAddDevice(false);
        setSeriNumarasi('');
        setMarka('');
        setModel('');
        setCihazSinifi('');
        setIsSgk(false);
        setZimmetTarihi('');
        setFaturaNo('');
        setVerilisTarihi('');
        setGarantiBaslangic('');
        setGarantiBitis('');
        setPeriyodikBakimTarihi('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleCustomerChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            setSelectedCustomer(null);
            setCustomerInputValue(newValue);
        } else if (newValue && newValue.inputValue) {
            // Yeni müşteri seçildi
            setSelectedCustomer(null);
            setCustomerInputValue(newValue.inputValue);
        } else {
            setSelectedCustomer(newValue);
            if (newValue && newValue.telefon) {
                setTelefon(newValue.telefon);
            }
        }
    };

    const handleSubmit = async () => {
        if (!randevuTarihi) {
            alert('Lütfen randevu tarihini seçiniz.');
            return;
        }

        const customerName = selectedCustomer?.adSoyad || customerInputValue;
        if (!customerName) {
            alert('Lütfen bir müşteri seçin veya müşteri adı girin.');
            return;
        }

        setIsSaving(true);

        try {
            let customerData = selectedCustomer ?
                { id: selectedCustomer.id, adSoyad: selectedCustomer.adSoyad, telefon: selectedCustomer.telefon } :
                null;

            // Yeni müşteri oluşturulacaksa
            if (!selectedCustomer && customerInputValue) {
                // Mükerrer cihaz denetimi (normalize edilmiş) — müşteri oluşturulmadan önce.
                if (addDevice && seriNumarasi && marka) {
                    const duplicate = await findDuplicateDeviceBySerial(seriNumarasi);
                    if (duplicate) {
                        alert(duplicateDeviceMessage(duplicate, seriNumarasi));
                        setIsSaving(false);
                        return;
                    }
                }

                const newCustomerRef = await addDoc(collection(db, "customers"), {
                    adSoyad: customerInputValue,
                    telefon: telefon,
                    hizmetTuru: addDevice ? 'Cihaz Sahibi' : 'Genel Müşteri',
                    email: '',
                    adres: '',
                    eklenmeTarihi: serverTimestamp()
                });
                customerData = { id: newCustomerRef.id, adSoyad: customerInputValue, telefon: telefon };
                // Yeni müşteri açık müşteri listelerinde (satış/servis seçicileri, Müşteriler)
                // anında görünsün diye cache'i geçersiz kıl.
                await invalidateCollections(['customers']);

                // Cihaz eklenecekse
                if (addDevice && seriNumarasi && marka) {
                    await addDoc(collection(db, "devices"), {
                        seriNumarasi: seriNumarasi.trim(),
                        marka,
                        model,
                        cihazSinifi,
                        isSgk,
                        zimmetTarihi,
                        faturaNo,
                        verilisTarihi,
                        garantiBaslangic,
                        garantiBitis,
                        periyodikBakimTarihi,
                        musteriId: customerData.id,
                        musteriAdSoyad: customerData.adSoyad,
                        eklenmeTarihi: serverTimestamp()
                    });

                    await syncBrandAndModel(marka, model);
                    // Yeni cihaz Cihazlar/Bakım/SGK listelerinde anında görünsün.
                    await invalidateCollections(['devices']);
                }
            }

            const appointmentData = {
                musteriId: customerData.id,
                musteriAdSoyad: customerData.adSoyad,
                musteriTelefon: customerData.telefon || telefon,
                randevuTarihi: Timestamp.fromDate(randevuTarihi),
                ilgilenilenHizmet,
                notlar,
            };

            if (isEditing) {
                await updateDoc(doc(db, "appointments", randevuToEdit.id), appointmentData);
                alert('Randevu başarıyla güncellendi!');
            } else {
                await addDoc(collection(db, "appointments"), {
                    ...appointmentData,
                    durum: 'Bekleniyor',
                    olusturmaTarihi: serverTimestamp(),
                    olusturanKullaniciId: currentUser.uid
                });
                alert('Randevu başarıyla oluşturuldu!');
            }
            handleClose();
        } catch (error) {
            console.error("Randevu kaydedilirken hata: ", error);
            alert('Bir hata oluştu.');
            setIsSaving(false);
        }
    };

    return {
        customers,
        selectedCustomer,
        customerInputValue,
        setCustomerInputValue,
        telefon,
        setTelefon,
        addDevice,
        setAddDevice,
        seriNumarasi,
        setSeriNumarasi,
        marka,
        setMarka,
        model,
        setModel,
        cihazSinifi,
        setCihazSinifi,
        isSgk,
        setIsSgk,
        zimmetTarihi,
        setZimmetTarihi,
        faturaNo,
        setFaturaNo,
        verilisTarihi,
        setVerilisTarihi,
        garantiBaslangic,
        setGarantiBaslangic,
        garantiBitis,
        setGarantiBitis,
        periyodikBakimTarihi,
        setPeriyodikBakimTarihi,
        brandsFromDb,
        randevuTarihi,
        setRandevuTarihi,
        ilgilenilenHizmet,
        setIlgilenilenHizmet,
        notlar,
        setNotlar,
        isSaving,
        handleCustomerChange,
        handleSubmit,
        handleClose,
        isEditing
    };
};
